import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Plus } from "lucide-react";
import api, { fmtErr } from "../../lib/api";

const LISTS = [
  { key: "team_members", label: "Team Members" },
  { key: "services", label: "Serviços" },
  { key: "payments", label: "Pagamentos" },
  { key: "statuses", label: "Estados" },
];

const CheckRow = ({ checked, onChange, label, testid }) => (
  <label className="flex items-center gap-2 text-sm font-light cursor-pointer">
    <input type="checkbox" data-testid={testid} checked={checked} onChange={(e) => onChange(e.target.checked)} /> {label}
  </label>
);

const AppSettings = () => {
  const cls =
    "bg-white border border-black/15 px-3 py-2.5 text-sm font-light focus:outline-none focus:border-black w-full";
  const [general, setGeneral] = useState({ show_prices: true });
  const [smtp, setSmtp] = useState(null);
  const [staffEmailsStr, setStaffEmailsStr] = useState("");
  const [testTo, setTestTo] = useState("");
  const [savingG, setSavingG] = useState(false);
  const [savingS, setSavingS] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then((res) => {
      setGeneral(res.data.general);
      setSmtp({ ...res.data.smtp, password: "" });
      setStaffEmailsStr((res.data.smtp.staff_emails || []).join(", "));
    });
  }, []);

  const saveGeneral = async (next) => {
    setGeneral(next);
    setSavingG(true);
    try {
      await api.put("/admin/settings/general", next);
      toast.success("Preferências guardadas.");
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail));
    } finally {
      setSavingG(false);
    }
  };

  const saveSmtp = async () => {
    setSavingS(true);
    try {
      const payload = {
        ...smtp,
        staff_emails: staffEmailsStr.split(",").map((s) => s.trim()).filter(Boolean),
      };
      delete payload.has_password;
      if (!payload.password) delete payload.password; // manter existente
      await api.put("/admin/settings/smtp", payload);
      toast.success("SMTP guardado.");
      setSmtp((s) => ({ ...s, password: "", has_password: s.has_password || !!payload.password }));
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail));
    } finally {
      setSavingS(false);
    }
  };

  const sendTest = async () => {
    if (!testTo.trim()) return toast.error("Indique um email para o teste.");
    setTesting(true);
    try {
      await api.post("/admin/settings/smtp/test", { to: testTo.trim() });
      toast.success("Email de teste enviado!");
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail));
    } finally {
      setTesting(false);
    }
  };

  if (!smtp) return <p className="label-editorial text-black/40">A carregar definições…</p>;

  return (
    <div className="space-y-8">
      {/* Preferências gerais */}
      <div data-testid="general-settings-panel" className="bg-white border border-black/5 p-6">
        <p className="label-editorial text-black/50 mb-4">Preferências</p>
        <CheckRow
          testid="show-prices-toggle"
          checked={general.show_prices}
          onChange={(v) => saveGeneral({ ...general, show_prices: v })}
          label="Mostrar preços no site e nos emails"
        />
        <p className="text-xs text-black/40 font-light mt-2">
          Se desligado, os preços deixam de aparecer no formulário de marcação e nos emails.
          {savingG && " A guardar…"}
        </p>
      </div>

      {/* SMTP / Emails */}
      <div data-testid="smtp-settings-panel" className="bg-white border border-black/5 p-6 space-y-5">
        <div className="flex items-center justify-between">
          <p className="label-editorial text-black/50">Emails (SMTP)</p>
          <CheckRow
            testid="smtp-enabled-toggle"
            checked={smtp.enabled}
            onChange={(v) => setSmtp({ ...smtp, enabled: v })}
            label="Emails ativos"
          />
        </div>
        <p className="text-sm text-black/40 font-light">
          Configure o seu servidor de email para enviar confirmações, avisos ao staff e lembretes.
          Ex.: Gmail — smtp.gmail.com, porta 587, STARTTLS, com uma <em>app password</em>.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <label className="text-xs uppercase tracking-widest text-black/40 block mb-1">Servidor (host)</label>
            <input data-testid="smtp-host" value={smtp.host} onChange={(e) => setSmtp({ ...smtp, host: e.target.value })} placeholder="smtp.gmail.com" className={cls} />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-black/40 block mb-1">Porta</label>
            <input data-testid="smtp-port" type="number" value={smtp.port} onChange={(e) => setSmtp({ ...smtp, port: parseInt(e.target.value, 10) || 0 })} placeholder="587" className={cls} />
          </div>
        </div>

        <div className="flex flex-wrap gap-6">
          <CheckRow testid="smtp-tls" checked={smtp.use_tls} onChange={(v) => setSmtp({ ...smtp, use_tls: v, use_ssl: v ? false : smtp.use_ssl })} label="STARTTLS (porta 587)" />
          <CheckRow testid="smtp-ssl" checked={smtp.use_ssl} onChange={(v) => setSmtp({ ...smtp, use_ssl: v, use_tls: v ? false : smtp.use_tls })} label="SSL (porta 465)" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs uppercase tracking-widest text-black/40 block mb-1">Utilizador</label>
            <input data-testid="smtp-username" value={smtp.username} onChange={(e) => setSmtp({ ...smtp, username: e.target.value })} placeholder="email@dominio.pt" className={cls} />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-black/40 block mb-1">
              Password {smtp.has_password && <span className="text-green-600 normal-case">(definida)</span>}
            </label>
            <input data-testid="smtp-password" type="password" value={smtp.password} onChange={(e) => setSmtp({ ...smtp, password: e.target.value })} placeholder={smtp.has_password ? "•••••••• (deixe vazio para manter)" : "app password"} className={cls} />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-black/40 block mb-1">Email de envio (remetente)</label>
            <input data-testid="smtp-from-email" value={smtp.from_email} onChange={(e) => setSmtp({ ...smtp, from_email: e.target.value })} placeholder="marcacoes@innpact.com.pt" className={cls} />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-black/40 block mb-1">Nome de envio</label>
            <input data-testid="smtp-from-name" value={smtp.from_name} onChange={(e) => setSmtp({ ...smtp, from_name: e.target.value })} placeholder="INN.PACT" className={cls} />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs uppercase tracking-widest text-black/40 block mb-1">Emails do staff (avisos de nova marcação)</label>
            <input data-testid="smtp-staff-emails" value={staffEmailsStr} onChange={(e) => setStaffEmailsStr(e.target.value)} placeholder="rececao@innpact.com.pt, severina@..." className={cls} />
            <p className="text-xs text-black/30 mt-1">Separe vários por vírgula.</p>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-black/40 block mb-1">Lembrete (horas antes)</label>
            <input data-testid="smtp-reminder-hours" type="number" min="1" max="168" value={smtp.reminder_hours} onChange={(e) => setSmtp({ ...smtp, reminder_hours: parseInt(e.target.value, 10) || 24 })} className={cls} />
          </div>
        </div>

        <div className="flex flex-wrap gap-6 pt-1">
          <CheckRow testid="smtp-send-client" checked={smtp.send_client_confirmation} onChange={(v) => setSmtp({ ...smtp, send_client_confirmation: v })} label="Confirmação ao cliente" />
          <CheckRow testid="smtp-send-staff" checked={smtp.send_staff_notification} onChange={(v) => setSmtp({ ...smtp, send_staff_notification: v })} label="Aviso ao staff" />
          <CheckRow testid="smtp-send-reminders" checked={smtp.send_reminders} onChange={(v) => setSmtp({ ...smtp, send_reminders: v })} label="Lembretes automáticos" />
        </div>

        <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-black/5">
          <button data-testid="smtp-save-btn" onClick={saveSmtp} disabled={savingS} className="label-editorial bg-black text-white px-8 py-3 hover:bg-black/80 transition-colors disabled:opacity-50">
            {savingS ? "A guardar…" : "Guardar SMTP"}
          </button>
          <div className="flex-1" />
          <input data-testid="smtp-test-email" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="email para teste" className={`${cls} max-w-[220px]`} />
          <button data-testid="smtp-test-btn" onClick={sendTest} disabled={testing} className="border border-black/20 px-6 py-3 hover:border-black transition-colors disabled:opacity-50 text-sm">
            {testing ? "A enviar…" : "Enviar teste"}
          </button>
        </div>
      </div>
    </div>
  );
};

export const SettingsAdmin = ({ menus, onSaved }) => {
  const [data, setData] = useState(menus);
  const [inputs, setInputs] = useState({});
  const [saving, setSaving] = useState(false);

  const addItem = (key) => {
    const value = (inputs[key] || "").trim();
    if (!value) return;
    if ((data[key] || []).includes(value)) return toast.error("Já existe na lista.");
    setData({ ...data, [key]: [...(data[key] || []), value] });
    setInputs({ ...inputs, [key]: "" });
  };

  const removeItem = (key, item) => {
    setData({ ...data, [key]: (data[key] || []).filter((v) => v !== item) });
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put("/admin/menus", data);
      onSaved(res.data);
      toast.success("Listas guardadas.");
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-8">
      <AppSettings />
      <p className="text-black/50 font-light max-w-xl">
        Edite as listas usadas na Folha de Caixa (equipa, serviços, meios de pagamento e estados).
      </p>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {LISTS.map(({ key, label }) => (
          <div key={key} data-testid={`settings-list-${key}`} className="bg-white border border-black/5 p-6">
            <p className="label-editorial text-black/50 mb-4">{label}</p>
            <div className="flex flex-wrap gap-2 mb-4">
              {(data[key] || []).map((item) => (
                <span key={item} className="flex items-center gap-2 border border-black/15 px-3 py-1.5 text-sm font-light">
                  {item}
                  <button
                    data-testid={`remove-${key}-${item.replace(/\s+/g, "-")}`}
                    onClick={() => removeItem(key, item)}
                    className="text-black/40 hover:text-red-600"
                    aria-label={`Remover ${item}`}
                  >
                    <X size={13} />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                data-testid={`add-input-${key}`}
                value={inputs[key] || ""}
                onChange={(e) => setInputs({ ...inputs, [key]: e.target.value })}
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addItem(key))}
                placeholder="Adicionar…"
                className="flex-1 bg-white border border-black/15 px-3 py-2.5 text-sm font-light focus:outline-none focus:border-black"
              />
              <button
                data-testid={`add-btn-${key}`}
                onClick={() => addItem(key)}
                className="border border-black/20 px-4 hover:border-black transition-colors"
                aria-label="Adicionar"
              >
                <Plus size={15} />
              </button>
            </div>
          </div>
        ))}
      </div>
      <button
        data-testid="settings-save-btn"
        onClick={save}
        disabled={saving}
        className="label-editorial bg-black text-white px-10 py-4 hover:bg-black/80 transition-colors disabled:opacity-50"
      >
        {saving ? "A guardar…" : "Guardar Listas"}
      </button>
    </div>
  );
};
